/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildApiUrl } from '../config/api';

/**
 * Custom React Query hook for fetching admin dashboard data
 * Optimized for fast loading with caching and progressive data loading
 */

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Fetch system stats
const fetchSystemStats = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    return {
      totalUsers: 0,
      totalJobSeekers: 0,
      totalRecruiters: 0,
      totalInterns: 0,
      totalEmployers: 0,
      totalInternships: 0,
      totalJobs: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      monthlyActiveUsers: 0,
      dailyActiveUsers: 0,
      todayVisitors: 0
    };
  }

  try {
    const response = await fetch(buildApiUrl('/api/admin/stats'), {
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error(`Failed to fetch stats: ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching system stats:', error);
    // Return default stats on error
    return {
      totalUsers: 0,
      totalJobSeekers: 0,
      totalRecruiters: 0,
      totalInterns: 0,
      totalEmployers: 0,
      totalInternships: 0,
      totalJobs: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      monthlyActiveUsers: 0,
      dailyActiveUsers: 0
    };
  }
};

/**
 * Main hook for fetching admin dashboard data
 * Uses React Query for caching and optimization
 */
export const useAdminDashboardData = () => {
  // System stats query - critical data, loads first
  // Optimized with longer stale time for better performance
  const statsQuery = useQuery({
    queryKey: ['adminStats'],
    queryFn: fetchSystemStats,
    staleTime: 30 * 1000, // 30 seconds - stats refresh more frequently for live visitor count
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    retry: 1,
    refetchOnMount: false, // Don't refetch if data exists in cache
    refetchOnWindowFocus: true, // Refetch on window focus to get latest visitor count
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds for live visitor counter
    // Use placeholder data for instant UI rendering
    placeholderData: (previousData) => previousData || {
      totalUsers: 0,
      totalJobSeekers: 0,
      totalRecruiters: 0,
      totalInterns: 0,
      totalEmployers: 0,
      totalInternships: 0,
      totalJobs: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      monthlyActiveUsers: 0,
      dailyActiveUsers: 0,
      todayVisitors: 0
    }
  });

  // Loading states
  const isCriticalDataLoading = statsQuery.isLoading;
  
  // Overall loading state (only true on initial load when no data exists)
  const isLoading = isCriticalDataLoading && !statsQuery.data;

  // Memoize stats data to prevent unnecessary recalculations
  const systemStats = useMemo(() => {
    return statsQuery.data || {
      totalUsers: 0,
      totalJobSeekers: 0,
      totalRecruiters: 0,
      totalInterns: 0,
      totalEmployers: 0,
      totalInternships: 0,
      totalJobs: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      monthlyActiveUsers: 0,
      dailyActiveUsers: 0,
      todayVisitors: 0
    };
  }, [statsQuery.data]);

  // Refetch function (for manual refresh)
  const refetch = async () => {
    await statsQuery.refetch();
  };

  return {
    systemStats,
    isLoading,
    isCriticalDataLoading,
    isError: statsQuery.isError,
    refetch,
    // Expose query for advanced usage
    statsQuery
  };
};

